import { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useResponse from '@/hooks/useResponse';
import AddOrEditTaskDialog from '@/modules/tasks/components/AddOrEditTaskDialog';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import useUpdateActionItem from '@/modules/tasks/features/actionItems/hooks/useUpdateActionItem';
import { UpdateActionItemInput } from '@/types/schema';

import { useTaskDetailsEditQuery } from '../../../graphql/queries/generated/taskDetailsEdit';
import { DialogProps } from '../types';

function EditTask({
  onClose,
  fileId,
  task: _taskToEdit,
  entity,
  entityId,
  participants,
  onRefetchParentProgress,
}: DialogProps) {
  const taskToEdit = _taskToEdit as ActionItemFragment;
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateActionItem } = useUpdateActionItem();
  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();

  const [{ fetching: isFetching, data }] = useTaskDetailsEditQuery({
    requestPolicy: 'network-only',
    pause: !taskToEdit?.id,
    variables: { filters: { actionItemId: taskToEdit?.id! } },
  });

  const onUpdateActionItem = useCallback(
    async (taskToUpdate: UpdateActionItemInput) => {
      setIsSubmitting(true);

      const data = await updateActionItem({
        data: taskToUpdate,
      });
      setIsSubmitting(false);
      handleResponse({
        condition: !!data.data?.updateActionItem?.actionItem?.id,
        onSuccess: () => {
          showSnackbar({
            message: t('Task:TaskUpdated'),
          });
          onRefetchParentProgress?.();
          onClose();
        },
      });
    },
    [handleResponse, onClose, onRefetchParentProgress, showSnackbar, t, updateActionItem],
  );

  return (
    <AddOrEditTaskDialog
      entity={entity}
      entityId={entityId}
      fileId={fileId}
      isFetching={isFetching}
      isSubmitting={isSubmitting}
      mode="edit"
      participants={participants}
      task={data?.actionItem?.actionItem}
      onClose={onClose}
      onUpdateActionItem={onUpdateActionItem}
    />
  );
}

export default EditTask;
